import React, { useState, useEffect } from 'react';
import {
  TextField,
  Box,
  Typography,
  Paper,
  InputAdornment,
  Alert,
  IconButton,
} from '@mui/material';
import { ContentCopy as ContentCopyIcon, Check as CheckIcon } from '@mui/icons-material';

interface ApiKeyConfigFieldsProps {
  apiKey: string;
  isLoading: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

// Function to generate a secure API key
const generateSecureApiKey = (): string => {
  const array = new Uint8Array(32);
  window.crypto.getRandomValues(array);
  return Array.from(array)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
};

export const ApiKeyConfigFields: React.FC<ApiKeyConfigFieldsProps> = ({
  apiKey,
  isLoading,
  onChange,
}) => {
  const [copied, setCopied] = useState(false);
  const [currentApiKey, setCurrentApiKey] = useState(apiKey);

  useEffect(() => {
    // Generate an API key if one doesn't exist
    if (!apiKey) {
      const newApiKey = generateSecureApiKey();
      setCurrentApiKey(newApiKey);
      
      // Update the parent component with the new API key
      const syntheticEvent = {
        target: {
          name: 'apiKey',
          value: newApiKey
        }
      } as React.ChangeEvent<HTMLInputElement>;
      
      onChange(syntheticEvent);
    }
  }, [apiKey, onChange]);

  const handleCopyClick = () => {
    navigator.clipboard.writeText(currentApiKey).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Content Management System (CMS) Integration
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        This method allows your CMS to push content directly to Location Station without requiring web scraping.
        Copy this API key and configure it in your CMS integration plugins or modules.
      </Alert>
      
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom fontWeight="medium">
          Your API Key (click to copy)
        </Typography>
        
        <TextField
          fullWidth
          variant="outlined"
          value={currentApiKey}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton 
                  onClick={handleCopyClick}
                  edge="end"
                  disabled={isLoading}
                >
                  {copied ? <CheckIcon color="success" /> : <ContentCopyIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          onClick={handleCopyClick}
          sx={{ 
            cursor: 'pointer',
            '.MuiInputBase-input': {
              fontFamily: 'monospace',
              letterSpacing: '0.5px'
            }
          }}
        />
        
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {copied ? 'Copied to clipboard!' : 'Click to copy to clipboard'}
        </Typography>
      </Paper>
      
      <Typography variant="subtitle1" gutterBottom>
        Integration Instructions
      </Typography>
      
      <Typography variant="body2" paragraph>
        1. Copy the API key above
      </Typography>
      
      <Typography variant="body2" paragraph>
        2. Configure your CMS to send content to:
        <Box component="code" sx={{ display: 'block', my: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
          https://yourinstance.location-station.com/api/cms/content
        </Box>
      </Typography>
      
      <Typography variant="body2" paragraph>
        3. Include the API key in the request header:
        <Box component="code" sx={{ display: 'block', my: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
          Authorization: Bearer {currentApiKey.substring(0, 10)}...
        </Box>
      </Typography>
      
      <Typography variant="body2">
        For detailed integration guides for popular CMS platforms, please refer to our documentation.
      </Typography>
    </Box>
  );
};

export default ApiKeyConfigFields;